import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../../contexts/UserContext/UserContext";
import { getEnabledSaleProviders } from "../../features/insurance/lib/insuranceSaleApi";
import ProductTour from "./ProductTour";
import { TourStepBody, TourStepCover, TourStepTitle } from "./TourStepContent";
import TOUR_STEPS from "./tourSteps";
import { isTourCompleted, markTourCompleted } from "./tourStorage";
import { delay, routesMatch, waitForTarget } from "./waitForTarget";
import PostOnboardingSetupFlow from "./PostOnboardingSetupFlow";
import {
    isPostOnboardingSetupPending,
    POST_ONBOARDING_SETUP_EVENT,
} from "./postOnboardingSetupStorage";

const ProductTourContext = createContext(null);

export const useProductTour = () => {
    const context = useContext(ProductTourContext);
    if (!context) {
        throw new Error("useProductTour must be used within a ProductTourProvider");
    }
    return context;
};

const getConnectionFlags = (companyData) => ({
    stripeConnected: Boolean(
        companyData?.defaultPaymentGateway ||
            companyData?.paymentGateways?.some((g) => g?.isConnected),
    ),
    insuranceConnected: Boolean(companyData?.insurance?.some((item) => item?.enabled === true)),
    salesEnabled: getEnabledSaleProviders(companyData).length > 0,
    // Gates the "Finish setup" step: the sidebar button it points at only renders while setup
    // is unfinished, so the step must disappear with it rather than highlight nothing.
    onboardingIncomplete: companyData?.onboardingSteps?.completed === false,
});

export const ProductTourProvider = ({ children }) => {
    const { state, myCompanyData } = useUser();
    const history = useHistory();
    const location = useLocation();
    const { t, ready } = useTranslation("productTour");

    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(0);
    const [postOnboardingSetupPending, setPostOnboardingSetupPending] = useState(isPostOnboardingSetupPending);

    const locationRef = useRef(location);
    const runIdRef = useRef(0);
    const openRef = useRef(false);
    const autoStartedForUserRef = useRef(null);
    const autoTourCancelledRef = useRef(false);
    const autoTourSchedulingRef = useRef(false);
    const returnToRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Keep a ref to the latest goToStep so button-click closures are never stale.
    const goToStepRef = useRef(async () => {});

    const userId = state?.user?.id;
    const displayName =
        state?.user?.firstName ||
        state?.user?.name?.split?.(" ")?.[0] ||
        myCompanyData?.name ||
        "";
    // Only start the tour once onboarding is finished (same guard as PrivateRoute)
    const isOnboardingComplete = Boolean(myCompanyData?.onboardingSteps?.completed);
    const flags = useMemo(() => getConnectionFlags(myCompanyData), [myCompanyData]);
    const visibleSteps = useMemo(
        () => TOUR_STEPS.filter((step) => !step.requiresFlag || flags[step.requiresFlag]),
        [flags],
    );

    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    useEffect(() => {
        openRef.current = open;
        document.body.classList.toggle("product-tour-open", open);
        return () => document.body.classList.remove("product-tour-open");
    }, [open]);

    // rc-tour measures the spotlight cutout once, when the step opens, and afterwards only
    // on window resize. A page that is still fetching when the step opens (the locations
    // table starts empty and grows once its rows arrive) would keep the stale, too-small
    // cutout for the rest of the step. Watch the live target and nudge rc-tour to re-measure
    // whenever it changes size.
    useEffect(() => {
        const selector = open ? visibleSteps[current]?.selector : null;
        if (!selector || typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const element = document.querySelector(selector);
        if (!element) {
            return undefined;
        }

        let frame = null;
        // ResizeObserver fires once on observe with the size rc-tour already measured.
        let isFirstEntry = true;

        const observer = new ResizeObserver(() => {
            if (isFirstEntry) {
                isFirstEntry = false;
                return;
            }
            if (frame) {
                cancelAnimationFrame(frame);
            }
            frame = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
            if (frame) {
                cancelAnimationFrame(frame);
            }
        };
    }, [current, open, visibleSteps]);

    const allocateRun = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        const rid = runIdRef.current + 1;
        runIdRef.current = rid;
        return rid;
    }, []);

    // ── Close ──────────────────────────────────────────────────────────────────
    const dismissOpenTourWithoutCompleting = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        runIdRef.current += 1;
        setOpen(false);
        openRef.current = false;
    }, []);

    const closeTour = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        runIdRef.current += 1; // cancel any in-flight goToStep
        setOpen(false);
        openRef.current = false;
        markTourCompleted(userId);

        const returnTo = returnToRef.current;
        returnToRef.current = null;
        if (returnTo && !routesMatch(locationRef.current, returnTo)) {
            history.push(returnTo);
        }
    }, [history, userId]);

    // ── Navigate to a specific step ────────────────────────────────────────────
    const goToStep = useCallback(
        async (index, direction, runId) => {
            // Stale-run guard
            if (runId !== runIdRef.current) return;

            // Past the end → finish tour
            if (index >= visibleSteps.length) {
                closeTour();
                return;
            }

            // Past the beginning → stay on step 0
            if (index < 0) {
                setCurrent(0);
                setOpen(true);
                return;
            }

            const step = visibleSteps[index];
            const signal = abortControllerRef.current?.signal;
            const needsNav = step.route && !routesMatch(locationRef.current, step.route);

            // Hide the popup during the transition
            setOpen(false);

            if (needsNav) {
                history.push(step.route);
                // Give React time to start mounting the new page before polling.
                await delay(400, signal);
            }

            if (runId !== runIdRef.current) return;

            if (step.selector) {
                const element = await waitForTarget(step.selector, {
                    timeout: 2500,
                    interval: 100,
                    signal,
                });

                if (runId !== runIdRef.current) return;

                if (!element) {
                    // Target not found on this page — skip to next/prev step.
                    await goToStepRef.current(index + direction, direction, runId);
                    return;
                }

                element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
                await delay(120, signal);
            } else {
                // No-target step (welcome / finish): just a short pause.
                await delay(150, signal);
            }

            if (runId !== runIdRef.current) return;

            setCurrent(index);
            setOpen(true);
        },
        [closeTour, history, visibleSteps],
    );

    // Keep ref current so stale button-click closures can still call the latest version.
    goToStepRef.current = goToStep;

    // ── Build antd step objects ────────────────────────────────────────────────
    // Each step's Next / Prev buttons drive navigation directly (not via onChange),
    // which sidesteps the ambiguity of whether antd's onChange fires with the
    // new index or the previous index.
    const antdSteps = useMemo(() => {
        if (!ready) return [];

        return visibleSteps.map((step, index) => {
            const useConnected = step.connectedFlag && flags[step.connectedFlag];
            const isFirst = index === 0;
            const isLast = index === visibleSteps.length - 1;
            const isHero = step.id === "welcome" || step.id === "finish";
            const highlights = t(`steps.${step.id}.highlights`, { returnObjects: true, defaultValue: [] });
            const tip = t(`steps.${step.id}.tip`, { defaultValue: "" });
            const greeting = isFirst
                ? displayName
                    ? t("steps.welcome.greeting", { name: displayName })
                    : t("steps.welcome.greetingAnonymous")
                : undefined;

            const onNext = () => {
                const rid = allocateRun();
                if (isLast) {
                    closeTour();
                } else {
                    goToStepRef.current(index + 1, 1, rid);
                }
            };

            const onPrev = () => {
                const rid = allocateRun();
                goToStepRef.current(index - 1, -1, rid);
            };

            return {
                title: isHero ? t(`steps.${step.id}.title`) : (
                    <TourStepTitle stepId={step.id} title={t(`steps.${step.id}.title`)} />
                ),
                description: (
                    <TourStepBody
                        greeting={greeting}
                        description={t(`steps.${step.id}.${useConnected ? "connectedDescription" : "description"}`)}
                        highlights={Array.isArray(highlights) && highlights.length > 0 ? highlights : undefined}
                        tip={tip || undefined}
                        tipLabel={t("tip")}
                    />
                ),
                cover: isHero ? (
                    <TourStepCover
                        stepId={step.id}
                        kicker={isFirst ? t("guidedTour") : t("youreReady")}
                        variant={isLast ? "finish" : "welcome"}
                    />
                ) : null,
                className: isHero ? "product-tour-hero" : undefined,
                target: step.selector ? () => document.querySelector(step.selector) : null,
                placement: step.placement,
                nextButtonProps: {
                    children: (
                        <span className="inline-flex items-center gap-1">
                            {isLast ? t("finish") : isFirst ? t("letsGo") : t("next")}
                            {isLast ? null : <ArrowRightOutlined />}
                        </span>
                    ),
                    onClick: onNext,
                },
                prevButtonProps: isFirst
                    ? { style: { display: "none" } }
                    : { children: t("back"), onClick: onPrev },
            };
        });
    }, [allocateRun, closeTour, displayName, flags, ready, t, visibleSteps]);

    // The settings panels are lazy chunks (see CompSettings). The tour only
    // waits 2.5s for a target, so on a cold cache the settings steps can time
    // out and silently skip. Warming the chunks when the tour starts removes
    // that race without lengthening the timeout for every other step.
    const prefetchLazyTourChunks = useCallback(() => {
        import("../../features/settings/components/integration/Integration").catch(() => {});
        import("../../features/settings/components/bookingSite/BookingSite").catch(() => {});
    }, []);

    // ── Start / replay ─────────────────────────────────────────────────────────
    const startTour = useCallback(async () => {
        if (!ready) return;

        prefetchLazyTourChunks();

        // Remember where the user was so we can put them back afterwards.
        returnToRef.current = `${locationRef.current.pathname}${locationRef.current.search}`;

        const rid = allocateRun();

        // Always start from the dashboard.
        if (!routesMatch(locationRef.current, "/")) {
            history.push("/");
            await delay(400, abortControllerRef.current?.signal);
        }

        if (rid !== runIdRef.current) return;

        await goToStepRef.current(0, 1, rid);
    }, [allocateRun, history, prefetchLazyTourChunks, ready]);

    const isPostOnboardingSetupActive = useCallback(
        () => postOnboardingSetupPending || isPostOnboardingSetupPending(),
        [postOnboardingSetupPending],
    );

    const activatePostOnboardingSetup = useCallback(() => {
        setPostOnboardingSetupPending(true);
        autoTourCancelledRef.current = true;
        autoTourSchedulingRef.current = false;
        dismissOpenTourWithoutCompleting();
        if (!isTourCompleted(userId)) {
            autoStartedForUserRef.current = null;
        }
    }, [dismissOpenTourWithoutCompleting, userId]);

    useEffect(() => {
        const syncPostOnboardingSetup = () => {
            if (isPostOnboardingSetupPending()) {
                activatePostOnboardingSetup();
            }
        };

        syncPostOnboardingSetup();
        window.addEventListener(POST_ONBOARDING_SETUP_EVENT, syncPostOnboardingSetup);
        return () => window.removeEventListener(POST_ONBOARDING_SETUP_EVENT, syncPostOnboardingSetup);
    }, [activatePostOnboardingSetup]);

    const scheduleAutoTour = useCallback(() => {
        if (!ready || !userId || !state?.isAuthenticated || !isOnboardingComplete) {
            return;
        }

        if (isPostOnboardingSetupPending()) {
            return;
        }

        if (autoStartedForUserRef.current === userId) {
            return;
        }

        if (isTourCompleted(userId)) {
            autoStartedForUserRef.current = userId;
            return;
        }

        if (autoTourSchedulingRef.current) {
            return;
        }

        autoTourSchedulingRef.current = true;
        autoTourCancelledRef.current = false;

        (async () => {
            await delay(600);
            autoTourSchedulingRef.current = false;

            if (
                autoTourCancelledRef.current ||
                openRef.current ||
                isTourCompleted(userId) ||
                isPostOnboardingSetupPending()
            ) {
                return;
            }

            autoStartedForUserRef.current = userId;
            await startTour();
        })();
    }, [isOnboardingComplete, ready, startTour, state?.isAuthenticated, userId]);

    const handlePostOnboardingSetupComplete = useCallback(() => {
        setPostOnboardingSetupPending(false);
    }, []);

    const handlePostOnboardingReadyForTour = useCallback(() => {
        autoTourCancelledRef.current = false;
        if (!isTourCompleted(userId)) {
            autoStartedForUserRef.current = null;
        }
        scheduleAutoTour();
    }, [scheduleAutoTour, userId]);

    // ── Auto-start on first authenticated visit (after onboarding) ───────────
    useEffect(() => {
        if (isPostOnboardingSetupActive()) {
            return undefined;
        }

        scheduleAutoTour();

        return () => {
            autoTourCancelledRef.current = true;
        };
    }, [isPostOnboardingSetupActive, postOnboardingSetupPending, scheduleAutoTour]);

    // ── Context value ──────────────────────────────────────────────────────────
    const value = useMemo(
        () => ({ startTour, closeTour, open }),
        [closeTour, open, startTour],
    );

    return (
        <ProductTourContext.Provider value={value}>
            {children}
            <PostOnboardingSetupFlow
                enabled={postOnboardingSetupPending}
                onComplete={handlePostOnboardingSetupComplete}
                onReadyForTour={handlePostOnboardingReadyForTour}
            />
            {ready ? (
                <ProductTour
                    open={open}
                    current={current}
                    steps={antdSteps}
                    // onChange is intentionally a no-op: all navigation is
                    // driven by nextButtonProps.onClick / prevButtonProps.onClick.
                    onChange={() => {}}
                    onClose={closeTour}
                    onFinish={closeTour}
                    onSkip={closeTour}
                    skipLabel={t("skip")}
                    progressLabel={t("progress")}
                />
            ) : null}
        </ProductTourContext.Provider>
    );
};

ProductTourProvider.propTypes = {
    children: PropTypes.node,
};
